// Custom next/image loader.
//
// Using ANY custom loader turns OFF Vercel's built-in Image Optimization
// pipeline (`/_next/image`), which is what Vercel bills as
// "Image Optimization – Transformations". Instead of routing every image
// through that (metered) optimizer, this loader builds `srcset` URLs that point
// at our own Directus asset proxy (`app/api/directus-assets/[fileId]`). The
// proxy resizes via Directus and caches each variant for 30 days, so the actual
// optimization work happens on infrastructure we already run.
//
// Recommended: front the Directus host (directus.madebysdk.com) with Cloudflare
// so the resized outputs are CDN-cached and Directus/Vercel serve almost none of
// the repeat traffic.
//
// The proxy already whitelists the `width`, `height`, `fit`, `quality`, and
// `format` params it accepts (see the route handler's ALLOWED_TRANSFORM_PARAMS),
// so the URLs this loader emits are safe to hit directly.

type DirectusImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
};

// A single canonical quality keeps the number of distinct Directus renders (and
// Cloudflare cache entries) down. 75 is visually indistinguishable from 80 for
// photographic content and matches the audit's "collapse to one quality"
// recommendation, so we intentionally ignore per-image `quality` props here.
const CANONICAL_QUALITY = 75;

const PROXY_PREFIX = "/api/directus-assets/";

/**
 * Detect crop intent from a getAssetUrl() query string.
 * Crop when `fit` is present, or when both width and height are present.
 * Returns the target aspect ratio (height / width) so we can scale height with
 * every srcset width Next asks for — not a single fixed height.
 */
function getCropAspectRatio(searchParams: URLSearchParams): number | null {
  const srcWidth = Number(searchParams.get("width"));
  const srcHeight = Number(searchParams.get("height"));
  const fit = searchParams.get("fit");

  const hasDimensions =
    Number.isFinite(srcWidth) &&
    srcWidth > 0 &&
    Number.isFinite(srcHeight) &&
    srcHeight > 0;
  const hasCropIntent = Boolean(fit) || hasDimensions;

  if (!hasCropIntent || !hasDimensions) {
    return null;
  }

  return srcHeight / srcWidth;
}

export default function directusImageLoader({
  src,
  width,
}: DirectusImageLoaderProps): string {
  // Only rewrite our own Directus proxy paths. Anything else (absolute URLs,
  // other static assets) is returned untouched so the browser loads it as-is.
  if (!src.startsWith(PROXY_PREFIX)) {
    return src;
  }

  const [path, query = ""] = src.split("?");
  const incoming = new URLSearchParams(query);
  const cropAr = getCropAspectRatio(incoming);

  const params = new URLSearchParams({
    width: String(width),
    quality: String(CANONICAL_QUALITY),
    format: "webp",
  });

  // Preserve crop at every pixel density: height scales with the srcset width.
  // Do not forward withoutEnlargement — it can force blur on smaller originals.
  if (cropAr !== null) {
    params.set("height", String(Math.round(width * cropAr)));
    params.set("fit", incoming.get("fit") || "cover");
  }

  return `${path}?${params.toString()}`;
}
